/**
 * The posting guide's model: which fields a campaign's leads carry, what the
 * operator has said about each (description, example, in or out of the
 * guide, and the `source_path` intake reads it from), and the guide built
 * from them.
 *
 * Two sources, merged:
 *
 * - `/api/v1/lead-fields` for the campaign: every key seen on its recent
 *   leads, with coverage. That is what suppliers really send.
 * - `settings.fields`: what the operator declared. A declared field never
 *   seen is still listed.
 *
 * Saving writes declared fields back, plus any seen field the operator has
 * touched, so a field nobody configured stays out of `settings.fields`.
 *
 * Examples never show a supplier someone's personal data. A field whose key
 * says it is personal (email, phone, name, address, postcode, IP...) gets a
 * placeholder. Any other field shows its commonest recent value only when
 * enough leads share it, and only if the value itself does not look like an
 * email, phone number, postcode, URL or IP address.
 */
import type { components } from '../api/schema';
import type { Vocab } from '../vocab';

type S = components['schemas'];
export type DeclaredField = S['CampaignField'];
export type SeenField = S['LeadField'];
export type FieldValue = S['LeadFieldValue'];

/** One row of the guide: a field seen, declared, or both. */
export type GuideField = {
  key: string;
  type: string;
  source_path: string;
  description: string;
  /** The operator's own example; empty means one is worked out. */
  example: string;
  in_guide: boolean;
  /** In `settings.fields` already. */
  declared: boolean;
  /** Share of sampled leads carrying it, 0 to 1; null when never seen. */
  coverage: number | null;
  leads_with: number;
  /** Changed since loaded, so a seen field is declared on save. */
  touched: boolean;
};

/**
 * Keys a lead carries that a supplier does not post: bookkeeping the server
 * adds, values routing works out, and the campaign id, which the guide shows
 * on its own.
 */
const NOT_POSTED = new Set([
  'id',
  'raw_request',
  'supplier_name',
  'score',
  'sold',
  'campaign_id',
  'campaignId',
  'api_key',
  'apiKey',
  'custom_fields',
]);

/** Seen keys the guide leaves out until the operator includes them. */
const OFF_BY_DEFAULT = new Set(['revenue']);

export const FIELD_TYPES = ['text', 'number', 'boolean', 'email', 'phone', 'date'];

/** A value shared by fewer leads than this is never shown as an example. */
export const MIN_SHARED = 5;

// ---------------------------------------------------------------------------
// Placeholders
// ---------------------------------------------------------------------------

/** Placeholders for keys that name personal data, matched in order. */
const PERSONAL: Array<[RegExp, string]> = [
  [/e_?mail/i, 'jane@example.com'],
  [/phone|mobile|^tel$|_tel$|telephone/i, '07700 900123'],
  [/^(first_?name|forename|given_?name)$/i, 'Jane'],
  [/^(last_?name|surname|family_?name)$/i, 'Doe'],
  [/^(name|full_?name|customer_?name|contact_?name)$/i, 'Jane Doe'],
  [/post_?code|zip/i, 'SW1A 1AA'],
  [/(^|_)ip(_?address)?$/i, '192.0.2.10'],
  [/street|address|^line_?\d|^house_?(number|name|no)?$|^flat|^building/i, '1 Example Street'],
  [/town|city|county|locality/i, 'London'],
  [/dob|birth/i, '1980-01-31'],
  [/uprn/i, '123456789012'],
  [/^url$|_url$|landing|referr?er/i, 'https://www.example.com/landing-page'],
  [/gclid|fbclid|msclkid|click_?id|cookie|session|device_?id|user_?agent/i, 'example-id-123'],
];

/**
 * Keys that mention a personal field but hold a flag or a category about it,
 * such as `marketing_consent_email` or `phone_type`.
 */
const NOT_PERSONAL = /consent|opt_?(in|out)|optin|optout|verified|valid|status|_type$|_count$|_score$|_match$/i;

/** The placeholder for a key that names personal data, or null. */
export function personalPlaceholder(key: string): string | null {
  if (NOT_PERSONAL.test(key)) return null;
  for (const [re, placeholder] of PERSONAL) {
    if (re.test(key)) return placeholder;
  }
  return null;
}

const EMAIL_RE = /[^\s@]+@[^\s@]+\.[^\s@]+/;
const PHONE_RE = /^(?:\+?44|0)\d{9,10}$/;
const POSTCODE_RE = /\b[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}\b/i;
const URL_RE = /^(https?:\/\/|www\.)/i;
const IP_RE = /^\d{1,3}(\.\d{1,3}){3}$|^[0-9a-f]{0,4}(:[0-9a-f]{0,4}){2,7}$/i;

/** What a value looks like, when it looks personal: a placeholder for it. */
export function personalValue(value: string): string | null {
  const v = value.trim();
  if (EMAIL_RE.test(v)) return 'jane@example.com';
  if (URL_RE.test(v)) return 'https://www.example.com/landing-page';
  if (IP_RE.test(v)) return '192.0.2.10';
  if (PHONE_RE.test(v.replace(/[\s()-]/g, ''))) return '07700 900123';
  if (POSTCODE_RE.test(v)) return 'SW1A 1AA';
  return null;
}

/** The example for a type when nothing better is known. */
export function typePlaceholder(type: string): string {
  switch (type) {
    case 'number':
      return '123';
    case 'boolean':
      return 'true';
    case 'email':
      return 'jane@example.com';
    case 'phone':
      return '07700 900123';
    case 'date':
      return '2026-01-31';
    default:
      return 'example';
  }
}

/** Whether a key's example comes from a placeholder, so no values are read. */
export function needsValues(field: Pick<GuideField, 'key' | 'example' | 'leads_with'>): boolean {
  return !field.example.trim() && field.leads_with > 0 && personalPlaceholder(field.key) === null;
}

/**
 * The example the guide shows for a field: the operator's own, else a
 * placeholder for a personal key, else the commonest recent value if enough
 * leads share it and it does not look personal, else one for the type.
 */
export function exampleFor(
  field: Pick<GuideField, 'key' | 'type' | 'example'>,
  values?: FieldValue[],
): { value: string; from: 'set' | 'placeholder' | 'leads' | 'type' } {
  if (field.example.trim()) return { value: field.example.trim(), from: 'set' };
  const placeholder = personalPlaceholder(field.key);
  if (placeholder) return { value: placeholder, from: 'placeholder' };
  if (field.type === 'email' || field.type === 'phone') {
    return { value: typePlaceholder(field.type), from: 'placeholder' };
  }
  const top = (values ?? []).find((v) => v.value.trim() !== '');
  if (top && top.count >= MIN_SHARED && top.value.length <= 60) {
    const looks = personalValue(top.value);
    if (looks) return { value: looks, from: 'placeholder' };
    return { value: top.value, from: 'leads' };
  }
  return { value: typePlaceholder(field.type), from: 'type' };
}

/** A type for a field first seen on leads, from its key and values. */
export function inferType(key: string, values?: FieldValue[]): string {
  if (!NOT_PERSONAL.test(key) && /e_?mail/i.test(key)) return 'email';
  if (!NOT_PERSONAL.test(key) && /phone|mobile|telephone/i.test(key)) return 'phone';
  const vs = (values ?? []).map((v) => v.value.trim()).filter(Boolean);
  if (vs.length === 0) return 'text';
  if (vs.every((v) => /^-?\d+(\.\d+)?$/.test(v)) && !/uprn|id$|code/i.test(key)) return 'number';
  if (vs.every((v) => /^(true|false)$/i.test(v))) return 'boolean';
  if (vs.every((v) => /^\d{4}-\d{2}-\d{2}/.test(v))) return 'date';
  return 'text';
}

// ---------------------------------------------------------------------------
// Merging and saving
// ---------------------------------------------------------------------------

/**
 * The guide rows: seen fields in the order `/lead-fields` gives them (built-in
 * first, then commonest), then declared fields never seen, in their stored
 * order. A built-in key that no lead carries and nobody declared is left out.
 */
export function mergeFields(declared: DeclaredField[], seen: SeenField[]): GuideField[] {
  const byName = new Map(declared.map((d) => [d.name, d]));
  const out: GuideField[] = [];
  const row = (key: string, d: DeclaredField | undefined, s: SeenField | undefined): GuideField => ({
    key,
    type: d?.type || 'text',
    source_path: d?.source_path ?? '',
    description: d?.description ?? '',
    example: d?.example ?? '',
    in_guide: d ? d.in_guide !== false : !OFF_BY_DEFAULT.has(key),
    declared: !!d,
    coverage: s && s.leads_with > 0 ? s.coverage : null,
    leads_with: s?.leads_with ?? 0,
    touched: false,
  });
  for (const s of seen) {
    if (s.computed || NOT_POSTED.has(s.key) || s.key.startsWith('__')) continue;
    const d = byName.get(s.key);
    if (s.leads_with === 0 && !d) continue;
    out.push(row(s.key, d, s));
  }
  for (const d of declared) {
    if (!out.some((r) => r.key === d.name)) out.push(row(d.name, d, undefined));
  }
  return out;
}

/**
 * What to send to `PUT /api/v1/campaigns/{id}/fields`: every declared field
 * and every seen one the operator changed, as the complete list. The server
 * lays each entry over the stored one, so legacy keys survive.
 */
export function fieldsToSave(rows: GuideField[]): DeclaredField[] {
  return rows
    .filter((r) => r.key.trim() && (r.declared || r.touched))
    .map((r) => ({
      name: r.key.trim(),
      type: r.type || 'text',
      source_path: r.source_path.trim(),
      description: r.description.trim(),
      example: r.example.trim(),
      in_guide: r.in_guide,
    }));
}

// ---------------------------------------------------------------------------
// The guide itself
// ---------------------------------------------------------------------------

/** An example value typed for JSON: numbers and booleans unquoted. */
export function jsonValue(type: string, value: string): unknown {
  if (type === 'number' && /^-?\d+(\.\d+)?$/.test(value) && value.length < 15) return Number(value);
  if (type === 'boolean' && /^(true|false)$/i.test(value)) return value.toLowerCase() === 'true';
  return value;
}

export type GuideEntry = { key: string; type: string; description: string; example: string };

/** The JSON body a supplier posts: the campaign id, then each field. */
export function exampleBody(campaignId: string, entries: GuideEntry[]): Record<string, unknown> {
  return {
    campaign_id: campaignId,
    ...Object.fromEntries(entries.map((e) => [e.key, jsonValue(e.type, e.example)])),
  };
}

/** Single quotes closed and reopened, so the body survives a POSIX shell. */
function shellQuote(s: string): string {
  return `'${s.replace(/'/g, `'\\''`)}'`;
}

export function curlExample(url: string, key: string, body: Record<string, unknown>): string {
  return [
    `curl -X POST ${shellQuote(url)} \\`,
    `  -H 'Content-Type: application/json' \\`,
    `  -H ${shellQuote(`X-API-Key: ${key}`)} \\`,
    `  -d ${shellQuote(JSON.stringify(body, null, 2))}`,
  ].join('\n');
}

export function jsExample(url: string, key: string, body: Record<string, unknown>): string {
  const pretty = JSON.stringify(body, null, 2);
  return `await fetch(${JSON.stringify(url)}, {\n  method: "POST",\n  headers: {\n    "Content-Type": "application/json",\n    "X-API-Key": ${JSON.stringify(key)},\n  },\n  body: JSON.stringify(${pretty}),\n});`;
}

export function pythonExample(url: string, key: string, body: Record<string, unknown>): string {
  const pretty = JSON.stringify(body, null, 4)
    .replace(/\btrue\b/g, 'True')
    .replace(/\bfalse\b/g, 'False')
    .replace(/\bnull\b/g, 'None');
  return `import requests\n\nrequests.post(\n    ${JSON.stringify(url)},\n    headers={"X-API-Key": ${JSON.stringify(key)}},\n    json=${pretty},\n)`;
}

/**
 * The whole guide as plain text, to paste into an email to a supplier. The
 * key is always the placeholder: it is sent separately.
 */
export function guideText(opts: {
  title: string;
  url: string;
  campaignId: string;
  keyRequired: boolean;
  entries: GuideEntry[];
  v: Vocab;
}): string {
  const { v } = opts;
  const body = exampleBody(opts.campaignId, opts.entries);
  const lines = [
    opts.title,
    '',
    `URL: POST ${opts.url}`,
    `Content type: application/json`,
    `campaign_id: ${opts.campaignId}`,
    `API key: send it in the X-API-Key header (${opts.keyRequired ? 'required' : 'recommended'}). It is sent separately.`,
    '',
    'Fields:',
    ...opts.entries.map(
      (e) => `- ${e.key}${e.description ? `: ${e.description}` : ''} (for example ${e.example})`,
    ),
    '',
    'Example request:',
    curlExample(opts.url, 'YOUR_API_KEY', body),
    '',
    `The response is 200 with a status of accepted, duplicate, suppressed or error. Branch on status, not on the HTTP code. A bad key, a missing field or an inactive ${v.one('campaign')} comes back as a 4xx.`,
  ];
  return lines.join('\n');
}
